import React from "react";

import { convertFeedsTabsLastVisitCounts } from "@/mk2/apps/blogs/helpers";
import {
    advancedArticleUrl,
    blogsAdvancedArticlePreviewGridUrl,
    blogsAlbumPhotoUrl,
    blogsArticleUrl,
    blogsAutoMessagePhotoUrl,
    blogsFriendsFeedUrl,
    blogsPopularFeedUrl,
    blogsShortMessagePhotoUrl,
    blogsUrl,
} from "@/mk2/apps/blogs/urls";
import { groupsGroupUrl, groupsPhotoDetailUrl } from "@/mk2/apps/groups/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Count } from "@/mk2/components/NotificationsBadge";
import { getAllPlaintext } from "@/mk2/helpers/article_ast/utils";
import { url } from "@/mk2/helpers/urls";
import {
    AdvancedArticleCategory,
    AdvancedArticleCategoryEntity,
    BlogPostEntity,
    BlogPostType,
    ImageEntity,
    PhotoEntity,
    UserEntity,
} from "@/mk2/schemas";
import {
    ARTICLE_IS_DRAFT,
    ARTICLE_TEXT,
    ARTICLE_TITLE,
    CHANGE_ARTICLE,
    HOWTO_DETAILS,
    HOWTO_EDIT_STEPS,
    HOWTO_IS_DRAFT,
    HOWTO_TEXT,
    HOWTO_TITLE,
    RECIPE_DETAILS,
    RECIPE_EDIT_DETAILS,
    RECIPE_EDIT_STEPS,
    RECIPE_IS_DRAFT,
    RECIPE_TEXT,
    RECIPE_TITLE,
    TAB_BLOGS_ALL,
    TAB_BLOGS_POPULAR,
    TAB_BLOGS_SUBSCRIBED,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID, FACEBOOK_APP_ID } from "@/mk/settings";

export const getModelAndId = (key: string): string[] => key.split("/");

export const canCreateBlogContests = (user: UserEntity) => {
    return (
        !!user.isAmbassadorOfBrandId || user.username === "redakce" || user.username === "redakcia"
    );
};

export function articleUrl(article: BlogPostEntity): string {
    if (article.advancedArticle) {
        return advancedArticleUrl(article);
    }
    const username = article.user?.username || article.author?.username;
    return url(blogsArticleUrl, { username, articleSlug: article.slug });
}

export function getAdvancedArticleCategory(post: BlogPostEntity): AdvancedArticleCategory {
    return (post?.advancedArticle?.category?.id || 0) as AdvancedArticleCategory;
}

export const articleTitleLabels: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: ARTICLE_TITLE,
    [AdvancedArticleCategory.RECIPES]: RECIPE_TITLE,
    [AdvancedArticleCategory.HOWTO]: HOWTO_TITLE,
};

export function getArticleTitleLabel(mainCategoryId: number): string {
    return articleTitleLabels[mainCategoryId];
}

const articleTextLabels: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: ARTICLE_TEXT,
    [AdvancedArticleCategory.RECIPES]: RECIPE_TEXT,
    [AdvancedArticleCategory.HOWTO]: HOWTO_TEXT,
};

export function getArticleTextLabel(mainCategoryId: number): string {
    return articleTextLabels[mainCategoryId];
}

const articleDetails: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: CHANGE_ARTICLE,
    [AdvancedArticleCategory.RECIPES]: RECIPE_DETAILS,
    [AdvancedArticleCategory.HOWTO]: HOWTO_DETAILS,
};

export function getArticleDetailsLabel(mainCategoryId: number): string {
    return articleDetails[mainCategoryId];
}

const editArticle: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: CHANGE_ARTICLE,
    [AdvancedArticleCategory.RECIPES]: RECIPE_EDIT_DETAILS,
    [AdvancedArticleCategory.HOWTO]: RECIPE_EDIT_DETAILS,
};

export function getEditArticleLabel(mainCategoryId): string {
    return editArticle[mainCategoryId];
}

const editArticleBody: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: CHANGE_ARTICLE,
    [AdvancedArticleCategory.RECIPES]: RECIPE_EDIT_STEPS,
    [AdvancedArticleCategory.HOWTO]: HOWTO_EDIT_STEPS,
};

export function getEditArticleBodyLabel(mainCategoryId): string {
    return editArticleBody[mainCategoryId];
}

const articleIsDraft: Record<AdvancedArticleCategory, string> = {
    [AdvancedArticleCategory.DEFAULT]: ARTICLE_IS_DRAFT,
    [AdvancedArticleCategory.RECIPES]: RECIPE_IS_DRAFT,
    [AdvancedArticleCategory.HOWTO]: HOWTO_IS_DRAFT,
};

export function getArticleIsDraftLabel(mainCategoryId): string {
    return articleIsDraft[mainCategoryId];
}

export function largestAvailablePhotoVersionOfBlogPost(
    photo: PhotoEntity,
    wishedVersion: "s720x720" | "s1600x1600",
) {
    return wishedVersion === "s1600x1600" ? "s1600x1600" : "s720x720";
}

export const canBeSetSticky = (post: BlogPostEntity): boolean => {
    return !!getAllPlaintext(post.perexAST);
};

export const getShortMessageHashtagLink = (hashtag: string): string => {
    return url(blogsUrl, {}, { h: hashtag, source: "message" });
};

export const getBlogsFeedUrlWithHashtagLink = (hashtag: string, source?: string): string => {
    return url(blogsUrl, {}, { query: `#${hashtag}`, source: source || "message" });
};

export const getGroupHashtagLink = (groupId: number, hashtag: string): string => {
    return url(groupsGroupUrl, { groupId }, { query: `#${hashtag}` });
};

export function initFacebookSdk() {
    // Prosim snazte sa reusovat tuto funkciu, nerobte vlastne kopie.
    // FB-api treba pravidelne updatovat, lebo ho pravidelne deprecatuju.
    // Tak nech sa to da robit na jednom mieste.
    const fbUrl = {
        201: "https://connect.facebook.net/sk_SK/sdk.js",
        202: "https://connect.facebook.net/cs_CZ/sdk.js",
    }[ACTIVE_SERVER_ID];
    // const fbUrl = 'https://connect.facebook.net/en_US/sdk/debug.js';

    // https://developers.facebook.com/docs/javascript/quickstart
    // eslint-disable
    const win: any = window;
    if (!win.FB) {
        win.fbAsyncInit = () => {
            win.FB.init({
                appId: FACEBOOK_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: "v8.0",
            });
        };

        // Load Facebook SDK for JavaScript
        (function (d, s, id) {
            const fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            if (!fjs) return;
            const js = d.createElement(s) as HTMLScriptElement;
            js.id = id;
            js.src = fbUrl;
            fjs.parentNode.insertBefore(js, fjs);
        })(document, "script", "facebook-jssdk");
    }
    // tslint:enable
}

export function destroyFacebookSdk() {
    const win: any = window;
    win.FB = null;
    document.getElementById("facebook-jssdk").remove();
}

export function photoDetailUrl(post: BlogPostEntity, image: ImageEntity): string {
    return post.group
        ? url(groupsPhotoDetailUrl, {
              groupId: post.group.id,
              postSlug: post.slug,
              imageId: image.id,
          })
        : post.type === BlogPostType.ALBUM
          ? url(blogsAlbumPhotoUrl, {
                username: post.author.username,
                postSlug: post.slug,
                imageId: image.id,
            })
          : post.type === BlogPostType.AUTOMATIC_MESSAGE
            ? url(blogsAutoMessagePhotoUrl, {
                  username: post.author.username,
                  postSlug: post.slug,
                  imageId: image.id,
              })
            : url(blogsShortMessagePhotoUrl, {
                  username: post.author.username,
                  postSlug: post.slug,
                  imageId: image.id,
              });
}

export function blogFeedHeaderTabs(
    active: string,
    requestUser: UserEntity,
    feedsTabsLastVisitCounts: { [tabName: string]: Count },
) {
    const { all, friends, popular } = convertFeedsTabsLastVisitCounts(feedsTabsLastVisitCounts);

    const tabSubscribed = (
        <Btn
            key="friends"
            type={BtnType.Tab}
            label={TAB_BLOGS_SUBSCRIBED}
            link={blogsFriendsFeedUrl}
            count={friends}
            active={active === "friends"}
        />
    );
    const tabPopular = (
        <Btn
            key="popular"
            type={BtnType.Tab}
            label={TAB_BLOGS_POPULAR}
            link={blogsPopularFeedUrl}
            count={popular}
            active={active === "popular"}
        />
    );
    const tabAll = (
        <Btn
            key="all"
            type={BtnType.Tab}
            label={TAB_BLOGS_ALL}
            link={blogsUrl}
            count={all}
            active={active === "all"}
        />
    );

    if (requestUser && isAuthenticated(requestUser)) {
        return [tabSubscribed, tabPopular, tabAll];
    } else {
        return [tabPopular, tabAll];
    }
}

export function userHasAdminPerm(inBlogs: boolean, requestUserPermissions: string[]) {
    // keep aligned with user_has_admin_perm(..)
    return inBlogs
        ? requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0
        : requestUserPermissions.indexOf("forum.can_manage_forum") >= 0; // can_manage_forum empower also in groups
}

export const getAdvnacedArticleBreadcrumbs = (
    category: AdvancedArticleCategoryEntity,
    username = null,
    hashtag = null,
) => {
    const breadcrumbInputs = [];
    let mainCategorySlug;
    let ancestorCategorySlug;
    let itemUrl;
    if (category?.parent?.parent?.id) {
        mainCategorySlug = category.parent.parent.slug;
        itemUrl = `/${mainCategorySlug}/`;
        breadcrumbInputs.push({
            itemUrl,
            itemLabel: category.parent.parent.name,
        });
    }
    if (category?.parent?.id) {
        if (!mainCategorySlug) {
            mainCategorySlug = category.parent.slug;
            itemUrl = `/${mainCategorySlug}/`;
        } else {
            ancestorCategorySlug = category.parent.slug;
            itemUrl = url(blogsAdvancedArticlePreviewGridUrl, {
                advancedArticleCategorySlug: mainCategorySlug,
                flag1: "c",
                value1: ancestorCategorySlug,
            });
        }
        breadcrumbInputs.push({
            itemUrl,
            itemLabel: category.parent.name,
        });
    }
    if (category?.id) {
        if (!mainCategorySlug) {
            mainCategorySlug = category.slug;
            itemUrl = `/${mainCategorySlug}/`;
        } else {
            ancestorCategorySlug = category.slug;
            itemUrl = url(blogsAdvancedArticlePreviewGridUrl, {
                advancedArticleCategorySlug: mainCategorySlug,
                flag1: "c",
                value1: ancestorCategorySlug,
            });
        }
        breadcrumbInputs.push({
            itemUrl,
            itemLabel: category.name,
        });
    }
    if (username) {
        if (mainCategorySlug) {
            itemUrl = url(blogsAdvancedArticlePreviewGridUrl, {
                advancedArticleCategorySlug: mainCategorySlug,
                flag1: "u",
                value1: username,
            });
        }
        breadcrumbInputs.push({
            itemUrl,
            itemLabel: username,
        });
    }
    if (hashtag) {
        breadcrumbInputs.push({
            itemUrl: null,
            itemLabel: `#${hashtag}`,
        });
    }

    return breadcrumbInputs;
};
